import React, { useEffect, useState } from "react";
import { Form, Input, Button, Card, Select, DatePicker, message, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { apiService } from "@/services/api";
import dayjs from "dayjs";

const { TextArea } = Input;

export const NotificationEdit: React.FC = () => {
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [status, setStatus] = useState<string>("DRAFT");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (!id) return;
      try {
        const data = await apiService.getNotifications(undefined, 1, 100);
        const notifications = data.data || data;
        const notification = (notifications as any[]).find(
          (n: any) => String(n.id) === String(id)
        );
        if (notification) {
          form.setFieldsValue({
            title: notification.title,
            type: notification.type,
            content: notification.content,
            audience: notification.audience,
            status: notification.status,
            scheduledDate: notification.scheduledDate ? dayjs(notification.scheduledDate) : undefined,
          });
          setStatus(notification.status || "DRAFT");
        }
      } catch (e) {
        message.error("Impossible de charger la notification");
      } finally {
        setFetchLoading(false);
      }
    })();
  }, [id]);

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const payload = {
        title: values.title,
        type: values.type,
        content: values.content,
        audience: values.audience,
        status: values.status,
        scheduledDate: values.scheduledDate?.toISOString(),
      };
      await apiService.updateNotification(Number(id), payload);
      message.success("Notification mise à jour");
      navigate("/notifications");
    } catch (e: any) {
      message.error("Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) return <Spin />;

  return (
    <Card title={`Modifier la notification #${id}`}>
      <Form layout="vertical" form={form} onFinish={onFinish} style={{ maxWidth: 600 }}>
        <Form.Item
          label="Titre"
          name="title"
          rules={[{ required: true, message: "Le titre est requis" }]}
        >
          <Input placeholder="Titre de la notification" maxLength={255} />
        </Form.Item>

        <Form.Item
          label="Type"
          name="type"
          rules={[{ required: true, message: "Le type est requis" }]}
        >
          <Select placeholder="Sélectionner un type">
            <Select.Option value="PUSH">Push</Select.Option>
            <Select.Option value="EMAIL">Email</Select.Option>
            <Select.Option value="SMS">SMS</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Message"
          name="content"
          rules={[{ required: true, message: "Le message est requis" }]}
        >
          <TextArea rows={4} placeholder="Contenu de la notification" maxLength={2000} />
        </Form.Item>

        <Form.Item
          label="Audience"
          name="audience"
          rules={[{ required: true, message: "L'audience est requise" }]}
        >
          <Select placeholder="Sélectionner l'audience cible">
            <Select.Option value="ALL">Tous les utilisateurs</Select.Option>
            <Select.Option value="PREMIUM">Utilisateurs Premium</Select.Option>
            <Select.Option value="FREE">Utilisateurs gratuits</Select.Option>
            <Select.Option value="NEW">Nouveaux utilisateurs</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Statut"
          name="status"
          rules={[{ required: true }]}
        >
          <Select onChange={setStatus}>
            <Select.Option value="DRAFT">Brouillon</Select.Option>
            <Select.Option value="SCHEDULED">Programmée</Select.Option>
            <Select.Option value="SENT">Envoyée</Select.Option>
          </Select>
        </Form.Item>

        {status === "SCHEDULED" && (
          <Form.Item
            label="Date d'envoi"
            name="scheduledDate"
            rules={[{ required: true, message: "La date est requise" }]}
          >
            <DatePicker
              showTime
              style={{ width: "100%" }}
              placeholder="Sélectionner date et heure"
            />
          </Form.Item>
        )}

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Enregistrer
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate("/notifications")}>
            Annuler
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default NotificationEdit;
